/* ===============================================
   CUSTOM PROJECTOR BUILDER JAVASCRIPT
   Handles live preview, size pricing (S: $30, M: $40, L: $50),
   distance calculations, email quote sending, and Shopify Draft Order API integration.
   =============================================== */

(function () {
  "use strict";

  // Projector Size Pricing Map organized by Type
  const PRICING = {
    indoor: [
      { key: "15W", label: "15W", price: 269.0, desc: "Compact indoor spaces" },
      { key: "25W", label: "25W", price: 289.0, desc: "Standard indoor display" },
      { key: "35W", label: "35W", price: 299.0, desc: "Bright indoor lighting", popular: true },
      { key: "50W", label: "50W", price: 339.0, desc: "High intensity indoor" },
    ],
    outdoor: [
      { key: "55W", label: "55W", price: 319.0, desc: "Weatherproof outdoor setup" },
      { key: "80W", label: "80W", price: 369.0, desc: "Storefront & outdoor facades", popular: true },
      { key: "100W", label: "100W", price: 419.0, desc: "High power outdoor projection" },
      { key: "120W", label: "120W", price: 489.0, desc: "Maximum power & wide distance" },
    ],
  };

  function getConfiguredScenes() {
    const root = document.getElementById("pb-builder-root");
    const fallback = [
      { name: "Diagram View", img: "assets/projector-image.png" },
      { name: "Club View", img: "assets/projector-club.png" },
      { name: "Restaurant View", img: "assets/projector-restaurant.png" },
    ];
    if (!root) return fallback;

    const configured = [];
    for (let i = 1; i <= 5; i++) {
      const url = root.dataset["scene" + i];
      if (url && url.trim()) {
        configured.push({ name: "Scene " + i, img: url.trim() });
      }
    }
    return configured;
  }

  function getDiscountConfig() {
    const root = document.getElementById("pb-builder-root");
    if (!root) return { enabled: false, type: "percentage", name: "Special Promo", value: "0" };

    const enabled = root.dataset.enableDiscount === "true";
    const type = root.dataset.discountType || "percentage";
    const name = root.dataset.discountName || "Special Promo";
    const value = root.dataset.discountValue || "0";

    return { enabled, type, name, value };
  }

  const PROJECTOR_SCENES = getConfiguredScenes();

  // State Management
  const state = {
    activeScene: PROJECTOR_SCENES[0],
    type: "outdoor", // 'indoor' | 'outdoor'
    size: "55W",
    logoFile: null,
    logoDataUrl: null,
    distance: 5, // in meters
    isCheckingOut: false,
    isSendingEmail: false,
  };

  // DOM Helper
  const el = (id) => document.getElementById(id);

  // Endpoint URLs
  function getDraftOrderEndpoint() {
    return "https://mediumvioletred-crab-837480.hostingersite.com/create-projector-draft-order.php";
  }

  // Toast Notification Helper
  function showToast(message, type = "info") {
    const container = el("pb-toast-container");
    if (!container) return;

    const toast = document.createElement("div");
    toast.className = `toast toast-${type}`;
    toast.textContent = message;

    container.appendChild(toast);
    setTimeout(() => {
      toast.style.opacity = "0";
      toast.style.transition = "opacity 0.3s ease";
      setTimeout(() => toast.remove(), 300);
    }, 3500);
  }

  // Calculate Price
  function getPrice() {
    const options = PRICING[state.type] || PRICING.indoor;
    const found = options.find((opt) => opt.key === state.size);
    return found ? found.price : options[0].price;
  }

  // Render Size Cards without size-badge-pill
  function renderSizeOptions() {
    const grid = el("pb-size-options-grid");
    if (!grid) return;

    const options = PRICING[state.type] || PRICING.indoor;
    
    // Validate state.size exists in current type options
    if (!options.some((opt) => opt.key === state.size)) {
      state.size = options[0].key;
    }

    grid.innerHTML = options
      .map((opt) => {
        const isActive = opt.key === state.size;
        return `
          <label class="size-card ${isActive ? "active" : ""}" id="pb-card-${opt.key}">
            <input type="radio" name="pb-size" value="${opt.key}" ${isActive ? "checked" : ""} class="size-radio">
            <div class="size-card-body">
              <div class="size-meta">
                <span class="size-title">${opt.label} ${opt.popular ? '<span class="popular-tag">Popular</span>' : ""}</span>
                <span class="size-desc">${opt.desc}</span>
              </div>
              <div class="size-price">$${opt.price}</div>
            </div>
          </label>
        `;
      })
      .join("");

    // Add change listeners to newly rendered radios
    grid.querySelectorAll("input[name='pb-size']").forEach((radio) => {
      radio.addEventListener("change", (e) => {
        state.size = e.target.value;
        renderSizeOptions();
        updateUI();
      });
    });
  }

  // Calculate Discount Amount
  function getDiscountAmount(originalPrice, discountConfig) {
    if (!discountConfig.enabled) return 0;
    const numVal = parseFloat(discountConfig.value) || 0;
    if (numVal <= 0) return 0;

    if (discountConfig.type === "fixed_amount" || discountConfig.type === "FIXED_AMOUNT") {
      return Math.min(numVal, originalPrice);
    } else {
      return (originalPrice * numVal) / 100;
    }
  }

  // Update UI Elements
  function updateUI() {
    const originalPrice = getPrice();
    const discountConfig = getDiscountConfig();
    const discountAmt = getDiscountAmount(originalPrice, discountConfig);
    const finalPrice = Math.max(0, originalPrice - discountAmt);

    // Footer Total Price Display
    const priceEl = el("pb-total-price");
    let badgeEl = el("pb-discount-badge");

    if (discountConfig.enabled && discountAmt > 0) {
      const discountTag = discountConfig.type === "fixed_amount" || discountConfig.type === "FIXED_AMOUNT"
        ? `$${parseFloat(discountConfig.value)} OFF`
        : `${parseFloat(discountConfig.value)}% OFF`;

      if (priceEl) {
        priceEl.innerHTML = `<span class="original-price-sub">$${originalPrice.toFixed(2)}</span> $${finalPrice.toFixed(2)}`;
      }
      if (badgeEl) {
        badgeEl.innerHTML = `<span class="discount-pill-tag">${discountTag}</span>`;
      }
    } else {
      if (priceEl) priceEl.textContent = `$${originalPrice.toFixed(2)}`;
      if (badgeEl) badgeEl.innerHTML = ``;
    }

    // Top Right Pricing & Discount Header
    const topPriceHeader = el("pb-top-price-header");
    if (topPriceHeader) {
      if (discountConfig.enabled && discountAmt > 0) {
        const discountTag = discountConfig.type === "fixed_amount" || discountConfig.type === "FIXED_AMOUNT"
          ? `$${parseFloat(discountConfig.value)} OFF`
          : `${parseFloat(discountConfig.value)}% OFF`;

        topPriceHeader.innerHTML = `
          <span class="top-price-label">Total Price:</span>
          <span class="original-price-strike">$${originalPrice.toFixed(2)}</span>
          <span class="discounted-price-highlight">$${finalPrice.toFixed(2)}</span>
          <span class="discount-pill-tag">${discountTag}</span>
        `;
      } else {
        topPriceHeader.innerHTML = `
          <span class="top-price-label">Total Price:</span>
          <span class="discounted-price-highlight">$${originalPrice.toFixed(2)}</span>
        `;
      }
    }

    // Distance Display & Badge
    const distDisplay = el("pb-distance-display");
    if (distDisplay) distDisplay.textContent = `${state.distance} Meters`;

    const distBadge = el("pb-distance-badge");
    if (distBadge) distBadge.textContent = `Type: ${state.type.toUpperCase()} | Size: ${state.size} | Distance: ${state.distance}m`;

    // Update Preview Background Image
    const previewPanel = el("pb-preview-panel");
    if (previewPanel) {
      if (state.activeScene && state.activeScene.img) {
        previewPanel.style.backgroundImage = `url("${state.activeScene.img}")`;
      } else {
        previewPanel.style.backgroundImage = "none";
      }
    }
  }

  // Render Scene Thumbnail Tiles
  function renderScenes() {
    const gallery = el("pb-scene-gallery");
    if (!gallery) return;
    gallery.innerHTML = "";

    if (!PROJECTOR_SCENES || PROJECTOR_SCENES.length === 0) {
      gallery.style.display = "none";
      return;
    }

    gallery.style.display = "";
    PROJECTOR_SCENES.forEach((scene) => {
      const thumb = document.createElement("button");
      thumb.type = "button";
      thumb.className = "scene-thumb" + (state.activeScene && scene.name === state.activeScene.name ? " active" : "");
      thumb.style.backgroundImage = `url("${scene.img}")`;
      thumb.title = scene.name;

      thumb.addEventListener("click", () => {
        state.activeScene = scene;
        renderScenes();
        updateUI();
      });

      gallery.appendChild(thumb);
    });
  }

  // Handle File Upload (Logo)
  function handleFileSelect(file) {
    if (!file || !file.type.startsWith("image/")) {
      showToast("Please upload a valid image file (PNG, JPG, SVG, WebP).", "error");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      showToast("File size exceeds 10MB limit.", "error");
      return;
    }

    state.logoFile = file;

    const reader = new FileReader();
    reader.onload = (e) => {
      state.logoDataUrl = e.target.result;

      // Update File Details in Dropzone
      const idleView = el("pb-upload-idle");
      const activeView = el("pb-upload-active");
      const thumb = el("pb-file-thumbnail");
      const nameEl = el("pb-file-name");
      const sizeEl = el("pb-file-size");

      if (idleView && activeView && thumb && nameEl && sizeEl) {
        idleView.style.display = "none";
        activeView.style.display = "flex";
        thumb.src = state.logoDataUrl;
        nameEl.textContent = file.name;
        sizeEl.textContent = (file.size / (1024 * 1024)).toFixed(2) + " MB";
      }

      showToast("Logo uploaded successfully!", "success");
    };

    reader.readAsDataURL(file);
  }

  // Remove File (Reset Dropzone)
  function removeFile() {
    state.logoFile = null;
    state.logoDataUrl = null;

    const fileInput = el("pb-logo-file");
    if (fileInput) fileInput.value = "";

    const idleView = el("pb-upload-idle");
    const activeView = el("pb-upload-active");
    if (idleView && activeView) {
      idleView.style.display = "flex";
      activeView.style.display = "none";
    }
  }

  // Draft Order API Integration (Shopify Checkout)
  async function handleCheckout() {
    if (state.isCheckingOut) return;

    if (!state.logoFile) {
      showToast("Please upload your logo before checkout.", "error");
      return;
    }

    const checkoutBtn = el("pb-checkout-btn");
    state.isCheckingOut = true;

    if (checkoutBtn) {
      checkoutBtn.disabled = true;
      checkoutBtn.innerHTML = `<span>Creating Order...</span>`;
    }

    try {
      const price = getPrice().toFixed(2);

      const properties = {
        "Projector Type": state.type === "outdoor" ? "Outdoor" : "Indoor",
        "Projector Size": `${state.size} Wattage`,
        "Projection Distance": `${state.distance} Meters`,
        "Logo Filename": state.logoFile ? state.logoFile.name : "Uploaded Logo",
        "Price": `$${price}`,
      };

      const discount = getDiscountConfig();

      const root = document.getElementById("pb-builder-root");
      const adminEmails = root ? root.dataset.adminEmail : "";

      const payload = {
        to_email: adminEmails,
        line_item: {
          title: `Custom ${state.type === "outdoor" ? "Outdoor" : "Indoor"} Projector (${state.size})`,
          price: price,
          quantity: 1,
          custom: true,
          properties: properties,
        },
      };

      if (discount.enabled && parseFloat(discount.value) > 0) {
        payload.appliedDiscount = {
          title: discount.name || "Special Promo",
          description: discount.type === "fixed_amount" || discount.type === "FIXED_AMOUNT"
            ? `$${discount.value} off your order`
            : `${discount.value}% off your order`,
          value: parseFloat(discount.value),
          valueType: (discount.type === "fixed_amount" || discount.type === "FIXED_AMOUNT") ? "FIXED_AMOUNT" : "PERCENTAGE"
        };
        // Also keep legacy fallback keys
        payload.discount_type = discount.type;
        payload.discount_value = discount.value;
      }

      const formData = new FormData();
      formData.append("payload", JSON.stringify(payload));
      if (state.logoFile) {
        formData.append("logo", state.logoFile);
      }

      const response = await fetch(getDraftOrderEndpoint(), {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || "Failed to create draft order.");
      }

      const result = await response.json();
      if (!result.success || !result.invoice_url) {
        throw new Error(result.message || "Draft order response missing invoice URL.");
      }

      // Redirect to Shopify Draft Order Checkout URL
      window.location.href = result.invoice_url;
    } catch (err) {
      console.error("Draft Order Checkout Error:", err);
      showToast("Could not create draft order: " + (err.message || "Please try again."), "error");
      state.isCheckingOut = false;
      if (checkoutBtn) {
        checkoutBtn.disabled = false;
        checkoutBtn.innerHTML = `<span>Proceed to Checkout</span>`;
      }
    }
  }

  // Event Wireup
  function wireEvents() {
    // Projector Type Segmented Control Buttons
    document.querySelectorAll(".type-segment-btn").forEach((btn) => {
      btn.addEventListener("click", () => {
        const val = btn.dataset.value;
        if (!val || val === state.type) return;

        state.type = val;

        // Update active class & ARIA
        document.querySelectorAll(".type-segment-btn").forEach((b) => {
          const isTarget = b.dataset.value === val;
          b.classList.toggle("active", isTarget);
          b.setAttribute("aria-selected", isTarget ? "true" : "false");
        });

        // Sync hidden input
        const hiddenInput = el("pb-type-select");
        if (hiddenInput) hiddenInput.value = val;

        renderSizeOptions();
        updateUI();
      });
    });

    // Distance Slider
    const distSlider = el("pb-distance-slider");
    if (distSlider) {
      distSlider.addEventListener("input", (e) => {
        const val = parseFloat(e.target.value);
        state.distance = val;
        document.querySelectorAll(".quick-dist-btn").forEach((b) => {
          if (parseFloat(b.dataset.dist) === val) {
            b.classList.add("active");
          } else {
            b.classList.remove("active");
          }
        });
        updateUI();
      });
    }

    // Distance Quick Buttons
    document.querySelectorAll(".quick-dist-btn").forEach((btn) => {
      btn.addEventListener("click", () => {
        document.querySelectorAll(".quick-dist-btn").forEach((b) => b.classList.remove("active"));
        btn.classList.add("active");
        const val = parseFloat(btn.dataset.dist);
        state.distance = val;
        if (distSlider) distSlider.value = val;
        updateUI();
      });
    });

    // File Input & Dropzone
    const dropzone = el("pb-dropzone");
    const fileInput = el("pb-logo-file");

    if (fileInput) {
      fileInput.addEventListener("change", (e) => {
        if (e.target.files && e.target.files[0]) {
          handleFileSelect(e.target.files[0]);
        }
      });
    }

    if (dropzone) {
      dropzone.addEventListener("dragover", (e) => {
        e.preventDefault();
        dropzone.classList.add("dragover");
      });
      dropzone.addEventListener("dragleave", () => {
        dropzone.classList.remove("dragover");
      });
      dropzone.addEventListener("drop", (e) => {
        e.preventDefault();
        dropzone.classList.remove("dragover");
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
          handleFileSelect(e.dataTransfer.files[0]);
        }
      });
    }

    // Remove File Button
    const removeBtn = el("pb-remove-logo");
    if (removeBtn) {
      removeBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        removeFile();
      });
    }

    // Checkout Button
    const checkoutBtn = el("pb-checkout-btn");
    if (checkoutBtn) {
      checkoutBtn.addEventListener("click", handleCheckout);
    }
  }

  // Initialize
  function init() {
    if (!el("pb-builder-root")) return;
    renderScenes();
    renderSizeOptions();
    wireEvents();
    updateUI();
  }

  document.readyState === "loading"
    ? document.addEventListener("DOMContentLoaded", init)
    : init();
})();
